import { RefObject, useEffect, useRef, useState } from "react";

/**
 * Height manager object returned by useEqualHeight hook
 */
export interface HeightManager {
  maxHeight: number;
  setMaxHeight: (height: number) => void;
  heightsRef: { current: number[] };
  updateHeight: (index: number, height: number) => void;
  resetHeights: () => void;
  syncOnMobile: boolean;
}

export interface EqualHeightOptions {
  syncOnMobile?: boolean;
}

/**
 * Element height manager object returned by useElementHeight hook
 */
export interface ElementHeightManager {
  elementRef: RefObject<HTMLDivElement | null>;
}

/**
 * Custom hook for equalizing heights of elements across multiple components
 * @param totalItems - Total number of items that will be measured
 * @param options - Configuration for height syncing behavior
 * @returns Height manager object with utilities for managing heights
 */
export const useEqualHeight = (
  totalItems: number = 0,
  options: EqualHeightOptions = {}
): HeightManager => {
  const { syncOnMobile = false } = options;
  const [maxHeight, setMaxHeight] = useState<number>(0);
  const heightsRef = useRef<number[]>([]);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize the heights array
  useEffect(() => {
    if (totalItems > 0) {
      heightsRef.current = new Array(totalItems).fill(0);
    }
  }, [totalItems]);

  const updateHeight = (index: number, height: number): void => {
    if (!heightsRef.current || index < 0) {
      return;
    }

    heightsRef.current[index] = height;

    // Small debounce to batch multiple rapid updates (e.g., from ResizeObserver)
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    updateTimeoutRef.current = setTimeout(() => {
      const newMaxHeight = Math.max(...heightsRef.current.filter((h) => h > 0));

      // Only update if maxHeight actually changed to prevent unnecessary re-renders
      if (newMaxHeight !== maxHeight && newMaxHeight > 0) {
        setMaxHeight(newMaxHeight);
      }
    }, 50); // Reduced from 150ms to 50ms for faster response
  };

  const resetHeights = (): void => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    setMaxHeight(0);
    heightsRef.current = new Array(totalItems).fill(0);
  };

  return {
    maxHeight,
    setMaxHeight,
    heightsRef,
    updateHeight,
    resetHeights,
    syncOnMobile,
  };
};

/**
 * Custom hook for individual element height management
 * @param index - Index of the current element
 * @param heightManager - Height manager object from useEqualHeight
 * @param dependencies - Dependencies to trigger height recalculation
 * @returns Object containing element ref for height management
 */
export const useElementHeight = (
  index: number,
  heightManager: HeightManager | undefined,
  dependencies: unknown[] = []
): ElementHeightManager => {
  const elementRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // Measure and update height continuously with ResizeObserver
  useEffect(
    () => {
      if (!elementRef.current || !heightManager) {
        return;
      }

      const element = elementRef.current;

      // Function to measure and update height
      const measureHeight = (): void => {
        if (!element) return;

        const height = element.scrollHeight;
        heightManager.updateHeight(index, height);
      };

      // Initial measurement
      measureHeight();

      // Setup ResizeObserver for continuous monitoring
      resizeObserverRef.current = new ResizeObserver(() => {
        window.requestAnimationFrame(() => {
          measureHeight();
        });
      });

      resizeObserverRef.current.observe(element);

      // Cleanup
      return () => {
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
        }
      };
    },
    // Spread operator allows flexible dependency injection from consumers (e.g., pricingView, rtbItems.length)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [index, heightManager, ...dependencies]
  );

  // Update element height when maxHeight changes
  useEffect(() => {
    if (elementRef.current && heightManager && heightManager.maxHeight > 0) {
      // Only apply height syncing on tablet and desktop (768px+) unless enabled
      // On mobile, let cards size naturally to avoid excessive white space
      const mediaQuery = window.matchMedia("(min-width: 768px)");

      const applyHeight = () => {
        if (!elementRef.current) return;

        const shouldSyncHeight =
          mediaQuery.matches || heightManager.syncOnMobile;

        if (shouldSyncHeight) {
          // Desktop/tablet (or mobile when enabled): sync heights
          elementRef.current.style.height = `${heightManager.maxHeight}px`;
        } else {
          // Mobile: clear fixed height, let content determine size
          elementRef.current.style.height = "";
        }
      };

      // Apply on mount and when media query changes
      applyHeight();
      mediaQuery.addEventListener("change", applyHeight);

      return () => {
        mediaQuery.removeEventListener("change", applyHeight);
      };
    }
  }, [heightManager, heightManager?.maxHeight, index]);

  return {
    elementRef,
  };
};
